// src/api.js
import axios from 'axios';
import { getBackendUrl } from './utils/apiUrl';
import { browserLogger } from './utils/browserLogger';

// Create an Axios instance with a base URL that respects the HTTPS setting
// Support runtime override from mobile WebView (window.REACT_APP_RXROOSTER_BACKEND_URL)
const useHttps = process.env.HTTPS === 'true';
const backendUrl = getBackendUrl();

// Convert http to https if HTTPS is enabled
const secureBackendUrl = useHttps && backendUrl.startsWith('http:') 
  ? backendUrl.replace('http:', 'https:') 
  : backendUrl;

console.log('API connecting to:', secureBackendUrl, 'HTTPS enabled:', useHttps);

const api = axios.create({
  baseURL: secureBackendUrl,
});

/**
 * Never send bearer-style URL credentials to browser diagnostics. Welcome
 * links are intentionally present in the request path, so an ordinary 410
 * must not copy the token into browserLogger, console exports, or support
 * bundles. Keep the route shape for debugging while replacing only the secret
 * segment. This also covers the token-bearing send-otp and confirm POSTs.
 */
export function sanitizeApiLogUrl(value) {
  if (typeof value !== 'string') return value;
  return value.replace(
    /(\/lead\/confirm\/)[^/?#]+/gi,
    '$1[REDACTED]',
  );
}

// ──────────────────────────────────────────────────────────────
// Dedupe + short-TTL cache for idempotent GETs.
// Coalesces concurrent identical requests (same URL + auth token) and serves
// recent responses from memory so sibling components don't re-hit the server.
// Invalidation:
//   • Token change busts the entire cache (logout/login)
//   • Any non-GET clears cache keys under the mutated path root
//   • Short TTLs keep staleness bounded
// Only URL patterns in DEDUPE_RULES opt in — everything else is untouched.
// ──────────────────────────────────────────────────────────────
// ttlMs      = how long fresh cache is returned without touching the network
// staleMs    = additional grace period where we return stale data AND kick off a background refresh
// (stale-while-revalidate). Page navigation inside a single session never blocks
// on these endpoints once warm.
const DEDUPE_RULES = [
  // A quote read can create the current quote when none exists. Billing
  // surfaces often mount as siblings, so concurrent reads must share that
  // first request rather than racing two quote creations. Keep the settled
  // value only briefly: checkout revalidates the count, and callers can
  // explicitly invalidate this entry when they need a fresh quote id.
  { pattern: /^\/billing\/quote(\?|$)/,                     ttlMs: 2_000,   staleMs: 0 },
  { pattern: /^\/auth\/me(\?|$)/,                           ttlMs: 60_000,  staleMs: 300_000 },
  { pattern: /^\/auth\/notification-preferences(\?|$)/,     ttlMs: 60_000,  staleMs: 300_000 },
  { pattern: /^\/provider\/me\/profile(\?|$)/,              ttlMs: 60_000,  staleMs: 300_000 },
  { pattern: /^\/provider\/[^/]+\/profile(\?|$)/,           ttlMs: 60_000,  staleMs: 300_000 },
  { pattern: /^\/notifications\/counts(\?|$)/,              ttlMs: 30_000,  staleMs: 120_000 },
  { pattern: /^\/notifications(\?|$)/,                      ttlMs: 30_000,  staleMs: 120_000 },
  { pattern: /^\/verification\/provider\/[^/]+(\?|$)/,      ttlMs: 120_000, staleMs: 300_000 },
  { pattern: /^\/credentialing\/vault(\?|$)/,               ttlMs: 30_000,  staleMs: 300_000 },
  { pattern: /^\/credentialing\/vault\/summary\/stats(\?|$)/, ttlMs: 30_000, staleMs: 300_000 },
  { pattern: /^\/credentialing\/vault\/resume-profile(\?|$)/, ttlMs: 60_000, staleMs: 300_000 },
  { pattern: /^\/referrals\/my-code(\?|$)/,                 ttlMs: 300_000, staleMs: 600_000 },
  { pattern: /^\/market-insights\/personalized(\?|$)/,      ttlMs: 60_000,  staleMs: 300_000 },
  { pattern: /^\/pipeline\/provider(\?|$)/,                 ttlMs: 30_000,  staleMs: 120_000 },
  // /listings/matches is expensive (seconds) — dedupe aggressively.
  // Cache includes query string, so filtered/paged requests are independent.
  { pattern: /^\/listings\/matches(\?|$)/,                  ttlMs: 30_000,  staleMs: 120_000 },
  { pattern: /^\/matching\/provider-matches(\?|$)/,         ttlMs: 30_000,  staleMs: 120_000 },
  { pattern: /^\/matching\/provider-swipes(\?|$)/,          ttlMs: 30_000,  staleMs: 120_000 },
  // Negotiation detail page reads. matchService.js also maintains its own
  // SWR cache, but adding the endpoints here gives us axios-level dedupe as
  // a second defense (e.g. against StrictMode double-mount racing the
  // service cache) and persists across full-page reloads via sessionStorage.
  { pattern: /^\/matching\/match\/[^/]+(\?|$)/,             ttlMs: 30_000,  staleMs: 120_000 },
  { pattern: /^\/negotiations\/state\/[^/]+\/available-transitions(\?|$)/, ttlMs: 30_000, staleMs: 120_000 },
  // Bootstrap aggregates everything above. Same backend TTL (30 s) + stale
  // window so a full reload can serve from sessionStorage immediately.
  { pattern: /^\/provider\/bootstrap(\?|$)/,                ttlMs: 30_000,  staleMs: 300_000 },
];

const __inflight = new Map(); // key -> Promise<AxiosResponse>
const __cache = new Map();    // key -> { response, expiresAt, staleUntil }
let __lastAuthToken = null;
// Monotonic cache generation. Any invalidation advances it so a GET that
// started before a mutation cannot repopulate the cache after that mutation.
// This complements __lastMutationAt (which protects seedCache) for ordinary
// axios reads.
let __cacheEpoch = 0;
// When the most recent mutation (any non-GET, or a manual invalidateCache) was
// observed. Read by seedCache to refuse a snapshot that was already stale when
// it arrived — see the comment on api.seedCache.
let __lastMutationAt = 0;

// ──────────────────────────────────────────────────────────────
// sessionStorage persistence layer.
//
// Without this, every full-page reload (what the profiler does, and what
// happens on browser refresh) starts with an empty cache — the first call on
// each page is always a cold miss. We persist cache entries to sessionStorage
// so a reload re-hydrates from the last good values, and the fresh-vs-stale
// TTL logic in dedupingGet decides whether to serve instantly or kick off a
// background refresh.
//
// Keyed by the same {token|url} tuple the in-memory cache uses; token change
// wipes both tiers together. Limited to cacheable endpoints (DEDUPE_RULES).
// ──────────────────────────────────────────────────────────────
const SESSION_STORAGE_PREFIX = 'rxr_api_cache:v1:';

function __sessionStorageAvailable() {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch {
    return false;
  }
}

function __hydrateFromSessionStorage() {
  if (!__sessionStorageAvailable()) return;
  const now = Date.now();
  const keysToRemove = [];
  try {
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const storageKey = window.sessionStorage.key(i);
      if (!storageKey || !storageKey.startsWith(SESSION_STORAGE_PREFIX)) continue;
      try {
        const raw = window.sessionStorage.getItem(storageKey);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') continue;
        if ((parsed.staleUntil || parsed.expiresAt || 0) < now) {
          keysToRemove.push(storageKey);
          continue;
        }
        const cacheKey = storageKey.slice(SESSION_STORAGE_PREFIX.length);
        __cache.set(cacheKey, {
          response: parsed.response,
          expiresAt: parsed.expiresAt,
          staleUntil: parsed.staleUntil,
        });
      } catch {
        keysToRemove.push(storageKey);
      }
    }
    for (const k of keysToRemove) window.sessionStorage.removeItem(k);
  } catch {
    /* storage may throw in some embeddings — ignore */
  }
}

function __persistToSessionStorage(cacheKey, entry) {
  if (!__sessionStorageAvailable()) return;
  // Cache axios response objects can be large — persist only the bits our
  // consumers touch on the other side.
  const response = entry.response;
  if (!response) return;
  try {
    const serializable = {
      response: {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: undefined, // never persist auth headers
        __fromCache: true,
      },
      expiresAt: entry.expiresAt,
      staleUntil: entry.staleUntil,
    };
    const s = JSON.stringify(serializable);
    // Skip anything larger than ~500 KB — dashboard payloads can get big and
    // sessionStorage has a ~5 MB budget that we share with other state.
    if (s.length > 500_000) return;
    window.sessionStorage.setItem(SESSION_STORAGE_PREFIX + cacheKey, s);
  } catch {
    /* quota exceeded or serialization failed — drop silently */
  }
}

function __purgeSessionStorage(cacheKey) {
  if (!__sessionStorageAvailable()) return;
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_PREFIX + cacheKey);
  } catch { /* ignore */ }
}

function __purgeAllSessionStorage() {
  if (!__sessionStorageAvailable()) return;
  try {
    const toRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(SESSION_STORAGE_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) window.sessionStorage.removeItem(k);
  } catch { /* ignore */ }
}

__hydrateFromSessionStorage();

function __matchRule(url) {
  return DEDUPE_RULES.find((r) => r.pattern.test(url));
}

// Invalidate only cache entries whose URL starts with the same top-level route as
// the mutated URL. Deliberately NOT falling back to "one-level-up" (an earlier
// version did, which produced '' for single-segment paths and silently wiped the
// entire cache on any POST /journey-events fire-and-forget completion).
function __invalidateByPrefix(url) {
  const root = (url || '').split('?')[0];
  if (!root) return;
  const topSegment = root.split('/').slice(0, 2).join('/'); // e.g. "/provider"
  if (!topSegment || topSegment === '/') return;
  __cacheEpoch += 1;
  const matches = (key) => {
    const bareKey = key.split('|')[1] || key;
    return (
      bareKey === root ||
      bareKey.startsWith(root + '/') ||
      bareKey.startsWith(root + '?') ||
      bareKey.startsWith(topSegment + '/') ||
      bareKey === topSegment
    );
  };
  for (const key of __cache.keys()) {
    if (matches(key)) {
      __cache.delete(key);
      __purgeSessionStorage(key);
    }
  }
  // Detach matching pre-mutation reads. They still resolve for their original
  // callers, but the epoch guard below prevents them from caching stale data,
  // and a post-mutation reader is free to issue a fresh request immediately.
  for (const key of __inflight.keys()) {
    if (matches(key)) __inflight.delete(key);
  }
}

// Seed the axios cache with data that came back from a different endpoint.
// Used by /provider/bootstrap — the server already aggregated the sub-responses,
// so there's no reason for the next `api.get('/auth/me')` or
// `api.get('/pipeline/provider')` to go over the wire. The calling code passes
// the canonical URL that a component would have fetched directly, plus the
// data shape that endpoint would have returned.
//
// readStartedAt is when the aggregate read was ISSUED, and passing it is what
// keeps a seed from resurrecting data a mutation has already replaced. A
// mutation busts the cache the moment its response lands (see the interceptor
// below), but bootstrap is fire-and-forget and SLOW — it aggregates the whole
// account — so it routinely lands AFTER a write that started later than it did.
// Without this guard it re-seeded its pre-write snapshot on top, with a full
// fresh TTL, and every reader saw the old values until the TTL expired.
//
// The cost of the bug was not theoretical: registration signs the provider in
// (which fires bootstrap) and then PATCHes the profile with the card they just
// built. Bootstrap's empty-profile snapshot landed second, so /provider/profile
// read 17% complete and the hero showed dashes for a profile the server had
// already filled in.
//
// Deliberately ONE global timestamp rather than per-route: a bootstrap snapshot
// is one read of the whole account, so any write that beat it home makes the
// whole snapshot suspect — including the /auth/me copy, which a write to
// /provider/* would never invalidate by prefix. Worst case we skip a seed and
// pay for the real reads.
api.seedCache = (url, data, readStartedAt) => {
  if (!url || data === undefined || data === null) return;
  if (Number.isFinite(readStartedAt) && readStartedAt < __lastMutationAt) return;
  const rule = __matchRule(url);
  if (!rule) return;
  const token = localStorage.getItem('authToken') || '';
  if (token !== __lastAuthToken) {
    __cacheEpoch += 1;
    __cache.clear();
    __inflight.clear();
    __purgeAllSessionStorage();
    __lastAuthToken = token;
  }
  const key = `${token}|${url}`;
  const entry = {
    response: { data, status: 200, statusText: 'OK', headers: {}, __fromBootstrap: true },
    expiresAt: Date.now() + rule.ttlMs,
    staleUntil: Date.now() + rule.ttlMs + (rule.staleMs || 0),
  };
  __cache.set(key, entry);
  __persistToSessionStorage(key, entry);
};

// Expose a manual cache buster for callers that mutate server state (e.g. after profile save).
api.invalidateCache = (urlPrefix) => {
  // Same standing as a mutation: the caller is telling us what is cached is
  // wrong, so an in-flight aggregate read that predates this call must not
  // reinstate it.
  __lastMutationAt = Date.now();
  __cacheEpoch += 1;
  if (!urlPrefix) {
    __cache.clear();
    __inflight.clear();
    __purgeAllSessionStorage();
    return;
  }
  for (const key of __cache.keys()) {
    const bareKey = key.split('|')[1] || key;
    if (bareKey.startsWith(urlPrefix)) {
      __cache.delete(key);
      __purgeSessionStorage(key);
    }
  }
  for (const key of __inflight.keys()) {
    const bareKey = key.split('|')[1] || key;
    if (bareKey.startsWith(urlPrefix)) __inflight.delete(key);
  }
};

// Wrap api.get to dedupe + cache matching URLs. All other methods pass through.
const __originalGet = api.get.bind(api);
// eslint-disable-next-line no-console
console.log('[api-dedupe] installed wrapper');
let __dedupeStats = { hits: 0, inflightMerges: 0, misses: 0, staleServed: 0 };
window.__apiDedupeStats = () => ({ ...__dedupeStats });
api.get = function dedupingGet(url, config = {}) {
  const rule = __matchRule(url);
  if (!rule) return __originalGet(url, config);

  const token = localStorage.getItem('authToken') || '';
  if (token !== __lastAuthToken) {
    __cacheEpoch += 1;
    __cache.clear();
    __inflight.clear();
    __purgeAllSessionStorage();
    __lastAuthToken = token;
  }

  const key = `${token}|${url}`;
  const now = Date.now();

  const cached = __cache.get(key);
  if (cached && cached.expiresAt > now) {
    __dedupeStats.hits += 1;
    return Promise.resolve({ ...cached.response, __fromCache: true });
  }

  // Stale-while-revalidate: if we're within the stale grace window, return the
  // stale value immediately AND kick off a background refresh (unless one is
  // already in flight). The user sees no blocking wait.
  if (cached && cached.staleUntil > now) {
    __dedupeStats.staleServed += 1;
    if (!__inflight.has(key)) {
      const requestEpoch = __cacheEpoch;
      let bg;
      bg = __originalGet(url, config)
        .then((response) => {
          if (requestEpoch === __cacheEpoch) {
            const entry = {
              response,
              expiresAt: Date.now() + rule.ttlMs,
              staleUntil: Date.now() + rule.ttlMs + (rule.staleMs || 0),
            };
            __cache.set(key, entry);
            __persistToSessionStorage(key, entry);
          }
          if (__inflight.get(key) === bg) __inflight.delete(key);
          return response;
        })
        .catch((err) => {
          if (__inflight.get(key) === bg) __inflight.delete(key);
          throw err;
        });
      __inflight.set(key, bg);
      // The stale reader has already received a response, so this background
      // request owns an error observer. Keep `bg` rejected for a foreground
      // reader that later joins it after the stale window expires.
      bg.catch(() => undefined);
    }
    return Promise.resolve({ ...cached.response, __fromCache: true, __stale: true });
  }

  if (__inflight.has(key)) {
    __dedupeStats.inflightMerges += 1;
    return __inflight.get(key);
  }
  __dedupeStats.misses += 1;

  const requestEpoch = __cacheEpoch;
  let p;
  p = __originalGet(url, config)
    .then((response) => {
      if (requestEpoch === __cacheEpoch) {
        const entry = {
          response,
          expiresAt: Date.now() + rule.ttlMs,
          staleUntil: Date.now() + rule.ttlMs + (rule.staleMs || 0),
        };
        __cache.set(key, entry);
        __persistToSessionStorage(key, entry);
      }
      if (__inflight.get(key) === p) __inflight.delete(key);
      return response;
    })
    .catch((err) => {
      if (__inflight.get(key) === p) __inflight.delete(key);
      throw err;
    });
  __inflight.set(key, p);
  return p;
};

// Invalidate cache on any successful mutating response.
api.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || 'get').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      __lastMutationAt = Date.now();
      __invalidateByPrefix(response.config?.url || '');
      // Provider readiness refetches after a successful save. Decoupled through
      // a window event so api.js never imports the readiness store (a cycle).
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('rxr:provider-mutation', {
            detail: { url: response.config?.url || '' },
          }));
        }
      } catch { /* non-browser environment */ }
    }
    return response;
  },
  (error) => Promise.reject(error),
);

// Capture x-trace-id from every response and surface failed requests to the
// browserLogger so debug mode picks them up.
api.interceptors.response.use(
  (response) => {
    const traceId = response.headers?.['x-trace-id'];
    if (traceId) browserLogger.setTraceId(traceId);
    return response;
  },
  (error) => {
    const traceId = error.response?.headers?.['x-trace-id'];
    if (traceId) browserLogger.setTraceId(traceId);
    browserLogger.error('api.request.failed', {
      url: sanitizeApiLogUrl(error.config?.url),
      status: error.response?.status,
      method: error.config?.method,
    });
    return Promise.reject(error);
  },
);

// Endpoints that a non-approved GROUP user must never call. Backend enforces
// with GroupApprovedGuard (403 + GROUP_NOT_APPROVED); blocking client-side here
// prevents futile traffic while the group is parked on /company/settings.
const GROUP_RESTRICTED_MATCHERS = [
  (m, u) => m === 'GET' && u.startsWith('/listings') && !u.startsWith('/listings/anonymous'),
  (m, u) => m === 'GET' && /^\/facilities\/[^/]+\/listings/.test(u),
  (m, u) => m === 'GET' && u === '/facilities/listings',
  (m, u) => m === 'GET' && u.startsWith('/matching/'),
  (m, u) => m !== 'GET' && u.startsWith('/listings'),
  (m, u) => m !== 'GET' && /^\/facilities\/[^/]+\/listings/.test(u),
];

const readGroupApprovalCache = () => {
  try {
    const raw = localStorage.getItem('groupApprovalCache');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.role !== 'GROUP') return null;
    if (!parsed.reviewStatus || parsed.reviewStatus === 'approved') return null;
    return parsed;
  } catch {
    return null;
  }
};

// Add a request interceptor to attach the JWT token and gate restricted calls
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Short-circuit restricted calls for non-approved groups.
    const payload = readGroupApprovalCache();
    if (payload) {
      const method = (config.method || 'GET').toUpperCase();
      const url = config.url || '';
      const isRestricted = GROUP_RESTRICTED_MATCHERS.some((fn) => fn(method, url));
      if (isRestricted) {
        try {
          window.dispatchEvent(
            new CustomEvent('showUnderReviewModal', {
              detail: {
                message: 'This action is not available while your account is under review.',
                reviewStatus: payload.reviewStatus,
                reviewReason: payload.reviewReason || '',
                reviewNotes: payload.reviewNotes || '',
                needsInfoFields: payload.needsInfoFields || [],
              },
            }),
          );
        } catch (e) { /* ignore */ }
        const err = new Error('GROUP_NOT_APPROVED');
        err.code = 'GROUP_NOT_APPROVED';
        err.isGroupApprovalBlock = true;
        return Promise.reject(err);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Surface backend 403s with GROUP_NOT_APPROVED code via the same modal event.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 403) {
      const body = error.response.data;
      const p = body?.message && typeof body.message === 'object' ? body.message : body;
      if (p?.code === 'GROUP_NOT_APPROVED') {
        try {
          window.dispatchEvent(
            new CustomEvent('showUnderReviewModal', {
              detail: {
                message: p.message || 'This action is not available for your account right now.',
                reviewStatus: p.reviewStatus || 'pending',
                reviewReason: p.reviewReason || '',
                reviewNotes: p.reviewNotes || '',
                needsInfoFields: p.needsInfoFields || [],
              },
            }),
          );
        } catch (e) { /* ignore */ }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
